import { getTeacherGoogleSyncStatus, startTeacherGoogleSync, TeacherGoogleSyncStatus } from "@/api/teacherGoogleSync";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const teacherGoogleSyncStatusQueryKey = ["teacherGoogleSyncStatus"] as const;

export function useTeacherGoogleSyncStatusQuery() {
	return useQuery({
		queryKey: teacherGoogleSyncStatusQueryKey,
		queryFn: getTeacherGoogleSyncStatus,
	});
}

export function useTeacherGoogleSyncMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: ["teacherGoogleSync"],
		mutationFn: startTeacherGoogleSync,
		onMutate: async () => {
			const previousData = queryClient.getQueryData<TeacherGoogleSyncStatus>(teacherGoogleSyncStatusQueryKey);
			await queryClient.cancelQueries({ queryKey: teacherGoogleSyncStatusQueryKey });

			queryClient.setQueryData<TeacherGoogleSyncStatus>(teacherGoogleSyncStatusQueryKey, {
				status: "running",
				startTime: new Date(),
			});

			return { previousData };
		},
		onError: (_err, _variables, context) => {
			if (context?.previousData !== undefined) {
				queryClient.setQueryData(teacherGoogleSyncStatusQueryKey, context.previousData);
			}
		},
		onSettled: () => {
			// IMPORTANT: Normally we'd invalidate the GET query key here (teacherGoogleSyncStatusQueryKey), but don't do that as it will cause an immediate refetch of status, which can cause the previous sync status to be received instead of the new sync status.
			// The reason for this being possible is that in the sync system, starting a sync + updating the sync status is not an atomic operation, so it's possible for the status to not be up-to-date when the mutation completes.
			// This may be resolved in the near future refactor to use sync-api for sync status, rather than account-management-service.
		},
	});
}
